import AddIcon from '@mui/icons-material/Add';
import { Alert, Button } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { KpiSection } from '../../../../data-models/company-financials.data-model';
import { EmptySection } from '../../../../components/EmptySection';
import { Loader } from '../../../../components/Loader/Loader';
import { ROUTES } from '../../../../constants/RouteNameMapping';
import {
  createCustomKpiTemplateDefaults,
  CustomTemplateSectionType,
  TemplateSectionFormValues,
} from '../../../../schemas/kpi-template.schema';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import { uuid } from '../../../../util/uuid';
import { templateDataModelToForm } from './custom-kpi-template-mappers';
import { CustomKpiTemplateForm } from './CustomKpiTemplateForm';
import { useConsolidatedTemplatesState } from './state/custom-template-state';

export const NewTemplateId = 'new' as const;

function createDefaultSections({ showFileSection = true } = {}): TemplateSectionFormValues[] {
  const sections: TemplateSectionFormValues[] = [
    { _key: uuid(), type: CustomTemplateSectionType.quantitative, sectionType: KpiSection.actual, kpis: [] },
    { _key: uuid(), type: CustomTemplateSectionType.textual, sectionType: KpiSection.actual, kpis: [] },
  ];
  if (showFileSection) {
    sections.push({
      _key: uuid(),
      type: CustomTemplateSectionType.file,
      sectionType: KpiSection.actual,
      kpis: [],
    });
  }
  sections.push({
    _key: uuid(),
    type: CustomTemplateSectionType.date,
    sectionType: KpiSection.actual,
    kpis: [],
  });
  return sections;
}

export function useCustomKpiTemplatePage(templateId: string | undefined) {
  const { showFileKpiConfig } = useFlags<MaggieFeatureFlags>();
  const { data: templates, isLoading, isFetching, isError } = useConsolidatedTemplatesState();

  const initialValues = useMemo(() => {
    const defaults = () => createDefaultSections({ showFileSection: showFileKpiConfig });
    if (!templateId || templateId === NewTemplateId) {
      return createCustomKpiTemplateDefaults({ sections: defaults() });
    }
    if (!templates) {
      return null;
    }
    const template = templates.find((t) => String(t.id) === templateId);
    if (!template) {
      return null;
    }
    const form = templateDataModelToForm(template);
    if (form.sections.length === 0) {
      form.sections = defaults();
    }
    return form;
  }, [templateId, templates, showFileKpiConfig]);

  const notFound = !isFetching && initialValues === null;

  return { initialValues, isLoading, isError, notFound };
}

export function CustomKpiTemplatePage() {
  const { templateId } = useParams<{ templateId: string }>();
  const { initialValues, isLoading, isError, notFound } = useCustomKpiTemplatePage(templateId);

  if (isError) {
    return (
      <Alert severity='error' sx={{ width: '100%', height: 'min-content' }}>
        Failed to load templates.
      </Alert>
    );
  }

  if (notFound) {
    return (
      <Alert severity='error' sx={{ width: '100%', height: 'min-content' }}>
        Template not found.
      </Alert>
    );
  }

  if (isLoading || !initialValues) {
    return <Loader />;
  }

  return <CustomKpiTemplateForm key={templateId} initialValues={initialValues} />;
}

function useCustomKpiTemplateRedirect() {
  const navigate = useNavigate();
  const { data: templates, isLoading } = useConsolidatedTemplatesState();

  const isResolving = isLoading || templates === undefined;
  const hasTemplates = !!templates && templates.length > 0;

  const handleNavigateNew = useCallback(() => {
    navigate(`/${ROUTES.KPI}/${ROUTES.KPI_CUSTOM_TEMPLATE_CONFIGURATION}/${NewTemplateId}`);
  }, [navigate]);

  useEffect(() => {
    if (isResolving) {
      return;
    }
    // Only auto-redirect when templates exist. When empty, render the empty state
    // below instead of navigating to /new (which would show "New Template").
    if (hasTemplates) {
      navigate(`/${ROUTES.KPI}/${ROUTES.KPI_CUSTOM_TEMPLATE_CONFIGURATION}/${templates![0].id}`, {
        replace: true,
      });
    }
  }, [templates, isResolving, hasTemplates, navigate]);

  return { isResolving, hasTemplates, handleNavigateNew };
}

export function CustomKpiTemplateRedirect() {
  const { isResolving, hasTemplates, handleNavigateNew } = useCustomKpiTemplateRedirect();

  if (isResolving || hasTemplates) {
    return <Loader />;
  }

  return (
    <EmptySection
      style={{ width: '100%' }}
      title='No Templates Yet'
      message='Create a template to start collecting KPI data from your portfolio companies.'
      actionComponent={
        <Button
          size='medium'
          variant='contained'
          color='secondary'
          startIcon={<AddIcon />}
          onClick={handleNavigateNew}
        >
          Add New Template
        </Button>
      }
    />
  );
}
